/**
 * UsersAdmin - Admin page for managing user approvals and airline assignments
 *
 * Features:
 * - View pending, approved, rejected, and all users
 * - Approve users with airline assignment
 * - Reject users with reason
 * - View user details and current memberships
 *
 * Permissions: Super Admin only (Global airline admin)
 *
 * Role in the onboarding architecture:
 * - PRIMARY path: Prospects pipeline (ProspectsAdmin) handles full onboarding.
 * - FALLBACK path: This page handles recovery when prospects/approve fails
 *   after generateLink (orphaned auth user with profiles.approval_status='pending').
 *   The super admin uses Approve here to complete what the API couldn't finish.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAirline } from '../../contexts/AirlineContext';
import {
  Check,
  X,
  Clock,
  Ban,
  Users,
  Building,
  Mail,
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ShieldOff,
  ShieldCheck,
  ChevronRight,
} from 'lucide-react';

// =====================================================================
// Types
// =====================================================================

interface PendingUser {
  user_id: string;
  approval_status: 'pending' | 'approved' | 'rejected' | 'revoked';
  requested_airline_id: string | null;
  signup_date: string;
  requested_airline_name: string | null;
  requested_airline_code: string | null;
  email: string;
  full_name: string;
  requested_role: string;
  email_verified: boolean;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  rejection_reason: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  revocation_reason: string | null;
  current_memberships: Array<{
    airline_id: string;
    airline_name: string;
    airline_code: string;
    role: string;
  }> | null;
}

interface Airline {
  id: string;
  name: string;
  code: string;
}

interface AirlineAssignment {
  airline_id: string;
  role: 'admin' | 'editor' | 'viewer';
}

type TabType = 'pending' | 'approved' | 'revoked' | 'rejected' | 'all';

// =====================================================================
// Main Component
// =====================================================================

export default function UsersAdmin() {
  const { isSuperAdmin } = useAirline();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showReinstateModal, setShowReinstateModal] = useState(false);

  // Inline email editing
  const [editingEmailFor, setEditingEmailFor] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string>('');
  const [savingEmail, setSavingEmail] = useState(false);

  async function saveEmail(userId: string) {
    const trimmed = pendingEmail.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      alert('Please enter a valid email address.');
      return;
    }
    setSavingEmail(true);
    try {
      // RPC updates both auth.users (login email) and public.users atomically,
      // and authorises airline admins + Global admins via SECURITY DEFINER.
      const { error } = await supabase.rpc('update_user_email', {
        target_user_id: userId,
        new_email: trimmed,
      });
      if (error) {
        alert(`Save failed: ${error.message}`);
        return;
      }
      setEditingEmailFor(null);
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    } finally {
      setSavingEmail(false);
    }
  }

  // ===================================================================
  // Fetch Users
  // ===================================================================

  // Fetch all users once — filtering is done in memory so cross-tab badge
  // counts are always accurate regardless of which tab is active.
  const { data: allUsers, isLoading, error: fetchError } = useQuery<PendingUser[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_all_users_admin')
        .select('*')
        .order('signup_date', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: isSuperAdmin,
  });

  // In-memory filter for the active tab
  const users = activeTab === 'all'
    ? allUsers
    : allUsers?.filter((u) => u.approval_status === activeTab);

  // Fetch all airlines for assignment
  const { data: airlines } = useQuery<Airline[]>({
    queryKey: ['airlines-for-assignment'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('airlines')
        .select('id, name, code')
        .neq('id', '00000000-0000-0000-0000-000000000000') // Exclude Global
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: isSuperAdmin,
  });

  // Counts for tab badges — always derived from the full unfiltered list
  const pendingCount = allUsers?.filter((u) => u.approval_status === 'pending').length ?? 0;
  const revokedCount = allUsers?.filter((u) => u.approval_status === 'revoked').length ?? 0;

  // ===================================================================
  // Mutations
  // ===================================================================

  const approveMutation = useMutation({
    mutationFn: async ({
      userId,
      memberships,
    }: {
      userId: string;
      memberships: AirlineAssignment[];
    }) => {
      // Call RPC function
      const { data, error } = await supabase.rpc('approve_user', {
        p_user_id: userId,
        p_airline_memberships: memberships,
      });

      if (error) throw error;

      // Send approval email
      try {
        const user = allUsers?.find((u) => u.user_id === userId) as PendingUser;
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const accessToken = session?.access_token;
        if (!accessToken) {
          throw new Error('Authentication required. Please sign in again.');
        }

        await fetch('/api/send-approval-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            user_id: user.user_id,
            action: 'approved',
            assigned_airlines: memberships.map((m) => {
              const airline = airlines?.find((a) => a.id === m.airline_id);
              return {
                name: airline?.name || 'Unknown',
                code: airline?.code || '',
                role: m.role,
              };
            }),
          }),
        });
      } catch (emailError) {
        console.error('Failed to send approval email:', emailError);
        // Don't throw - user is approved, email is secondary
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowApproveModal(false);
      setSelectedUser(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason: string }) => {
      const { data, error } = await supabase.rpc('reject_user', {
        p_user_id: userId,
        p_rejection_reason: reason || null,
      });

      if (error) throw error;

      // Send rejection email
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const accessToken = session?.access_token;
        if (!accessToken) {
          throw new Error('Authentication required. Please sign in again.');
        }

        await fetch('/api/send-approval-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            user_id: userId,
            action: 'rejected',
            rejection_reason: reason,
          }),
        });
      } catch (emailError) {
        console.error('Failed to send rejection email:', emailError);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowRejectModal(false);
      setSelectedUser(null);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason: string }) => {
      const { data, error } = await supabase.rpc('revoke_user', {
        p_user_id: userId,
        p_revocation_reason: reason || null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowRevokeModal(false);
      setSelectedUser(null);
    },
  });

  const reinstateMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.rpc('reinstate_user', {
        p_user_id: userId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowReinstateModal(false);
      setSelectedUser(null);
    },
  });

  // ===================================================================
  // Permission Check
  // ===================================================================

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need super admin permissions to access this page.</p>
        </div>
      </div>
    );
  }

  // ===================================================================
  // Helpers
  // ===================================================================

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            <Clock size={12} />
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle2 size={12} />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle size={12} />
            Rejected
          </span>
        );
      case 'revoked':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            <ShieldOff size={12} />
            Revoked
          </span>
        );
      default:
        return null;
    }
  };

  const getRoleBadge = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'bg-purple-100 text-purple-800',
      instructor: 'bg-blue-100 text-blue-800',
      trainee: 'bg-gray-100 text-gray-800',
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          colors[role] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {role}
      </span>
    );
  };

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div className="py-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          Review and approve user registrations, manage airline assignments
        </p>
      </div>

      {/* Tabs — mobile: vertical list; desktop: horizontal bar */}
      <div className="md:hidden bg-white rounded-xl shadow-sm border border-steel/20 overflow-hidden divide-y divide-steel/10 mb-6">
        {([
          { key: 'pending',  label: 'Pending',  icon: <Clock size={18} />,        badge: pendingCount > 0 ? { count: pendingCount, color: 'bg-red-500' } : null },
          { key: 'approved', label: 'Approved', icon: <CheckCircle2 size={18} />, badge: null },
          { key: 'revoked',  label: 'Revoked',  icon: <ShieldOff size={18} />,    badge: revokedCount > 0 ? { count: revokedCount, color: 'bg-amber-500' } : null },
          { key: 'rejected', label: 'Rejected', icon: <XCircle size={18} />,      badge: null },
          { key: 'all',      label: 'All users',icon: <Users size={18} />,        badge: null },
        ] as Array<{ key: TabType; label: string; icon: React.ReactNode; badge: { count: number; color: string } | null }>).map(({ key, label, icon, badge }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`relative w-full flex items-center gap-4 px-4 py-3.5 transition ${
              activeTab === key ? 'bg-orca-elevate/5' : 'hover:bg-ice'
            }`}
          >
            <span className={`absolute left-0 inset-y-0 w-1 rounded-r transition-colors ${activeTab === key ? 'bg-orca-elevate' : 'bg-transparent'}`} />
            <span className={activeTab === key ? 'text-orca-elevate' : 'text-gray-400'}>{icon}</span>
            <span className={`flex-1 text-left font-semibold text-sm ${activeTab === key ? 'text-orca-elevate' : 'text-navy'}`}>{label}</span>
            {badge && <span className={`${badge.color} text-white text-xs px-2 py-0.5 rounded-full`}>{badge.count}</span>}
            <ChevronRight size={16} className="text-steel/40 shrink-0" />
          </button>
        ))}
      </div>

      <div className="hidden md:block border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['pending', 'approved', 'revoked', 'rejected', 'all'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === tab
                  ? 'border-orca-elevate text-orca-elevate'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'pending'   && <Clock size={16} />}
              {tab === 'approved'  && <CheckCircle2 size={16} />}
              {tab === 'revoked'   && <ShieldOff size={16} />}
              {tab === 'rejected'  && <XCircle size={16} />}
              {tab === 'all'       && <Users size={16} />}
              <span className="capitalize">{tab}</span>
              {tab === 'pending' && pendingCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingCount}</span>
              )}
              {tab === 'revoked' && revokedCount > 0 && (
                <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">{revokedCount}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Error State */}
      {fetchError && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
          <div className="flex items-center gap-2">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">Failed to load users: {(fetchError as Error).message}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="animate-spin text-orca-elevate" size={32} />
          <span className="ml-3 text-gray-500">Loading users...</span>
        </div>
      ) : users && users.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="mx-auto text-gray-400" size={48} />
          <p className="mt-4 text-gray-600">
            No {activeTab === 'all' ? '' : activeTab} users found
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white shadow-md rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested Airline</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signup Date</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users?.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orca-elevate/10 flex items-center justify-center">
                          <span className="text-orca-elevate font-semibold">
                            {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.full_name || 'N/A'}</div>
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Mail size={12} />
                            {editingEmailFor === user.user_id ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="email"
                                  value={pendingEmail}
                                  onChange={(e) => setPendingEmail(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); saveEmail(user.user_id); }
                                    else if (e.key === 'Escape') setEditingEmailFor(null);
                                  }}
                                  autoFocus
                                  className="rounded border border-gray-300 px-2 py-0.5 text-sm"
                                />
                                <button type="button" disabled={savingEmail} onClick={() => saveEmail(user.user_id)} className="px-2 py-0.5 text-xs font-medium text-white bg-orca-elevate rounded disabled:opacity-50">
                                  {savingEmail ? '…' : 'Save'}
                                </button>
                                <button type="button" onClick={() => setEditingEmailFor(null)} className="px-2 py-0.5 text-xs text-gray-600">Cancel</button>
                              </div>
                            ) : (
                              <>
                                <span>{user.email}</span>
                                {user.email_verified && <CheckCircle2 size={12} className="text-green-500 ml-1" />}
                                <button type="button" onClick={() => { setPendingEmail(user.email); setEditingEmailFor(user.user_id); }} className="ml-2 text-xs text-[#4A90E2] hover:underline">Edit</button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">{getRoleBadge(user.requested_role || 'unknown')}</td>
                    <td className="px-4 py-4">
                      {user.requested_airline_name ? (
                        <div className="flex items-center gap-2">
                          <Building size={14} className="text-gray-400" />
                          <span className="text-sm text-gray-900">{user.requested_airline_name}</span>
                          <span className="text-xs text-gray-500">({user.requested_airline_code})</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">None specified</span>
                      )}
                    </td>
                    <td className="px-4 py-4">{getStatusBadge(user.approval_status)}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Calendar size={14} />
                        <span>{formatDate(user.signup_date)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {user.approval_status === 'pending' && (
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => { setSelectedUser(user); setShowApproveModal(true); }} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"><Check size={14} /> Approve</button>
                          <button onClick={() => { setSelectedUser(user); setShowRejectModal(true); }} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"><Ban size={14} /> Reject</button>
                        </div>
                      )}
                      {user.approval_status === 'approved' && (
                        <button onClick={() => { setSelectedUser(user); setShowRevokeModal(true); }} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-amber-700 border border-amber-300 bg-amber-50 rounded-md hover:bg-amber-100 transition-colors"><ShieldOff size={14} /> Revoke</button>
                      )}
                      {user.approval_status === 'revoked' && (
                        <button onClick={() => { setSelectedUser(user); setShowReinstateModal(true); }} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 border border-green-300 bg-green-50 rounded-md hover:bg-green-100 transition-colors"><ShieldCheck size={14} /> Reinstate</button>
                      )}
                      {user.approval_status === 'rejected' && (
                        <span className="text-sm text-gray-400">Rejected{user.rejection_reason && `: ${user.rejection_reason}`}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {users?.map((user) => (
              <div key={user.user_id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-4 pt-4 pb-2">
                  <div className="w-10 h-10 rounded-full bg-orca-elevate/10 flex items-center justify-center shrink-0">
                    <span className="text-orca-elevate font-semibold text-sm">{user.full_name?.charAt(0)?.toUpperCase() || '?'}</span>
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col items-start gap-0.5">
                    <p className="font-semibold text-gray-900 text-sm truncate w-full">{user.full_name || 'N/A'}</p>
                    {getStatusBadge(user.approval_status)}
                  </div>
                </div>

                {/* Meta */}
                <div className="flex flex-col gap-1.5 px-4 pb-3 text-xs text-gray-600">
                  <span className="flex items-center gap-2">
                    <Mail size={13} className="text-gray-400 shrink-0" />
                    {editingEmailFor === user.user_id ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        <input type="email" value={pendingEmail} onChange={(e) => setPendingEmail(e.target.value)} autoFocus className="rounded border border-gray-300 px-2 py-0.5 text-sm" />
                        <button type="button" disabled={savingEmail} onClick={() => saveEmail(user.user_id)} className="px-2 py-0.5 text-xs font-medium text-white bg-orca-elevate rounded disabled:opacity-50">{savingEmail ? '…' : 'Save'}</button>
                        <button type="button" onClick={() => setEditingEmailFor(null)} className="px-2 py-0.5 text-xs text-gray-600">Cancel</button>
                      </div>
                    ) : (
                      <span className="flex items-center gap-1 min-w-0">
                        <span className="truncate">{user.email}</span>
                        {user.email_verified && <CheckCircle2 size={11} className="text-green-500 shrink-0" />}
                        <button type="button" onClick={() => { setPendingEmail(user.email); setEditingEmailFor(user.user_id); }} className="text-[#4A90E2] hover:underline shrink-0 ml-1">Edit</button>
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    <Users size={13} className="text-gray-400 shrink-0" />
                    {getRoleBadge(user.requested_role || 'unknown')}
                  </span>
                  <span className="flex items-center gap-2">
                    <Building size={13} className="text-gray-400 shrink-0" />
                    {user.requested_airline_name
                      ? <>{user.requested_airline_name} <span className="text-gray-400">({user.requested_airline_code})</span></>
                      : <span className="text-gray-400">No airline specified</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar size={13} className="text-gray-400 shrink-0" />
                    {formatDate(user.signup_date)}
                  </span>
                </div>

                {/* Action bar */}
                <div className="border-t border-gray-100">
                  {user.approval_status === 'pending' && (
                    <div className="flex divide-x divide-gray-100">
                      <button onClick={() => { setSelectedUser(user); setShowApproveModal(true); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-green-700 hover:bg-green-50 transition"><Check size={14} /> Approve</button>
                      <button onClick={() => { setSelectedUser(user); setShowRejectModal(true); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-red-600 hover:bg-red-50 transition"><Ban size={14} /> Reject</button>
                    </div>
                  )}
                  {user.approval_status === 'approved' && (
                    <button onClick={() => { setSelectedUser(user); setShowRevokeModal(true); }} className="w-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-amber-700 hover:bg-amber-50 transition"><ShieldOff size={14} /> Revoke Access</button>
                  )}
                  {user.approval_status === 'revoked' && (
                    <button onClick={() => { setSelectedUser(user); setShowReinstateModal(true); }} className="w-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-green-700 hover:bg-green-50 transition"><ShieldCheck size={14} /> Reinstate Access</button>
                  )}
                  {user.approval_status === 'rejected' && (
                    <p className="text-xs text-gray-400 text-center py-2.5">Rejected{user.rejection_reason && `: ${user.rejection_reason}`}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedUser && (
        <ApproveUserModal
          user={selectedUser}
          airlines={airlines || []}
          onClose={() => {
            setShowApproveModal(false);
            setSelectedUser(null);
          }}
          onApprove={(memberships) =>
            approveMutation.mutate({ userId: selectedUser.user_id, memberships })
          }
          isLoading={approveMutation.isPending}
          error={approveMutation.error as Error | null}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedUser && (
        <RejectUserModal
          user={selectedUser}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedUser(null);
          }}
          onReject={(reason) => rejectMutation.mutate({ userId: selectedUser.user_id, reason })}
          isLoading={rejectMutation.isPending}
          error={rejectMutation.error as Error | null}
        />
      )}

      {/* Revoke Modal */}
      {showRevokeModal && selectedUser && (
        <RevokeUserModal
          user={selectedUser}
          onClose={() => { setShowRevokeModal(false); setSelectedUser(null); }}
          onRevoke={(reason) => revokeMutation.mutate({ userId: selectedUser.user_id, reason })}
          isLoading={revokeMutation.isPending}
          error={revokeMutation.error as Error | null}
        />
      )}

      {/* Reinstate Modal */}
      {showReinstateModal && selectedUser && (
        <ReinstateUserModal
          user={selectedUser}
          onClose={() => { setShowReinstateModal(false); setSelectedUser(null); }}
          onReinstate={() => reinstateMutation.mutate(selectedUser.user_id)}
          isLoading={reinstateMutation.isPending}
          error={reinstateMutation.error as Error | null}
        />
      )}
    </div>
  );
}

// =====================================================================
// Approve User Modal
// =====================================================================

interface ApproveUserModalProps {
  user: PendingUser;
  airlines: Airline[];
  onClose: () => void;
  onApprove: (memberships: AirlineAssignment[]) => void;
  isLoading: boolean;
  error: Error | null;
}

const ApproveUserModal: React.FC<ApproveUserModalProps> = ({
  user,
  airlines,
  onClose,
  onApprove,
  isLoading,
  error,
}) => {
  const [assignments, setAssignments] = useState<AirlineAssignment[]>(() => {
    // Pre-select requested airline if available
    if (user.requested_airline_id) {
      return [{ airline_id: user.requested_airline_id, role: 'viewer' as const }];
    }
    return [];
  });

  const toggleAirline = (airlineId: string) => {
    setAssignments((prev) => {
      const exists = prev.find((a) => a.airline_id === airlineId);
      if (exists) {
        return prev.filter((a) => a.airline_id !== airlineId);
      }
      return [...prev, { airline_id: airlineId, role: 'viewer' as const }];
    });
  };

  const updateRole = (airlineId: string, role: 'admin' | 'editor' | 'viewer') => {
    setAssignments((prev) =>
      prev.map((a) => (a.airline_id === airlineId ? { ...a, role } : a))
    );
  };

  const handleApprove = () => {
    if (assignments.length === 0) {
      return;
    }
    onApprove(assignments);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Approve User</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error.message}</p>
            </div>
          )}

          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-2">User Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Name:</span>{' '}
                <span className="font-medium">{user.full_name}</span>
              </div>
              <div>
                <span className="text-gray-500">Email:</span>{' '}
                <span className="font-medium">{user.email}</span>
              </div>
              <div>
                <span className="text-gray-500">Requested Role:</span>{' '}
                <span className="font-medium capitalize">{user.requested_role}</span>
              </div>
              <div>
                <span className="text-gray-500">Requested Airline:</span>{' '}
                <span className="font-medium">
                  {user.requested_airline_name || 'None specified'}
                </span>
              </div>
            </div>
          </div>

          {/* Airline Assignment */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">
              Assign Airlines <span className="text-red-500">*</span>
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Select one or more airlines and assign a role for each.
            </p>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {airlines.map((airline) => {
                const assignment = assignments.find((a) => a.airline_id === airline.id);
                const isSelected = !!assignment;
                const isRequested = airline.id === user.requested_airline_id;

                return (
                  <div
                    key={airline.id}
                    className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                      isSelected
                        ? 'border-orca-elevate bg-orca-elevate/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <label className="flex items-center gap-3 cursor-pointer flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleAirline(airline.id)}
                        className="w-4 h-4 text-orca-elevate border-gray-300 rounded focus:ring-orca-elevate"
                      />
                      <div className="flex items-center gap-2">
                        <Building size={16} className="text-gray-400" />
                        <span className="font-medium text-gray-900">{airline.name}</span>
                        <span className="text-sm text-gray-500">({airline.code})</span>
                        {isRequested && (
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                            Requested
                          </span>
                        )}
                      </div>
                    </label>

                    {isSelected && (
                      <select
                        value={assignment?.role || 'viewer'}
                        onChange={(e) =>
                          updateRole(airline.id, e.target.value as 'admin' | 'editor' | 'viewer')
                        }
                        className="text-sm border-gray-300 rounded-md focus:ring-orca-elevate focus:border-orca-elevate"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}
                  </div>
                );
              })}
            </div>

            {assignments.length === 0 && (
              <p className="mt-2 text-sm text-red-600">Please select at least one airline</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApprove}
            disabled={isLoading || assignments.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Approving...
              </>
            ) : (
              <>
                <Check size={16} />
                Approve & Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Reject User Modal
// =====================================================================

interface RejectUserModalProps {
  user: PendingUser;
  onClose: () => void;
  onReject: (reason: string) => void;
  isLoading: boolean;
  error: Error | null;
}

const RejectUserModal: React.FC<RejectUserModalProps> = ({
  user,
  onClose,
  onReject,
  isLoading,
  error,
}) => {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Reject User</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error.message}</p>
            </div>
          )}

          <p className="text-gray-600 mb-4">
            Are you sure you want to reject <strong>{user.full_name}</strong>'s registration request?
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rejection Reason (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full border-gray-300 rounded-md focus:ring-orca-elevate focus:border-orca-elevate"
              placeholder="Provide a reason for rejection..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onReject(reason)}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Rejecting...
              </>
            ) : (
              <>
                <Ban size={16} />
                Reject User
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Revoke User Modal
// =====================================================================

interface RevokeUserModalProps {
  user: PendingUser;
  onClose: () => void;
  onRevoke: (reason: string) => void;
  isLoading: boolean;
  error: Error | null;
}

const RevokeUserModal: React.FC<RevokeUserModalProps> = ({
  user,
  onClose,
  onRevoke,
  isLoading,
  error,
}) => {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <ShieldOff size={20} className="text-amber-600" /> Revoke Access
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error.message}</p>
            </div>
          )}
          <p className="text-gray-600 mb-4">
            Revoke access for <strong>{user.full_name}</strong>? Their profile and training history
            are preserved and access can be reinstated at any time.
          </p>
          {user.current_memberships && user.current_memberships.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
              Their airline memberships ({user.current_memberships.map(m => m.airline_name).join(', ')}) will be retained and restored on reinstatement.
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-gray-400">(recommended — stored in audit log)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full border-gray-300 rounded-md focus:ring-orca-elevate focus:border-orca-elevate"
              placeholder="e.g. Employment ended, leave of absence, compliance review…"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onRevoke(reason)}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50"
          >
            {isLoading ? (
              <><Loader2 size={16} className="animate-spin" /> Revoking…</>
            ) : (
              <><ShieldOff size={16} /> Revoke Access</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Reinstate User Modal
// =====================================================================

interface ReinstateUserModalProps {
  user: PendingUser;
  onClose: () => void;
  onReinstate: () => void;
  isLoading: boolean;
  error: Error | null;
}

const ReinstateUserModal: React.FC<ReinstateUserModalProps> = ({
  user,
  onClose,
  onReinstate,
  isLoading,
  error,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <ShieldCheck size={20} className="text-green-600" /> Reinstate Access
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
      </div>
      <div className="p-6">
        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error.message}</p>
          </div>
        )}
        <p className="text-gray-600 mb-4">
          Reinstate access for <strong>{user.full_name}</strong>? Their previous airline
          memberships will be restored immediately.
        </p>
        {user.current_memberships && user.current_memberships.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
            Will restore access to: {user.current_memberships.map(m => `${m.airline_name} (${m.role})`).join(', ')}
          </div>
        )}
        {user.revocation_reason && (
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
            <span className="font-medium">Previous revocation reason:</span> {user.revocation_reason}
          </div>
        )}
      </div>
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
        <button
          onClick={onClose}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onReinstate}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          {isLoading ? (
            <><Loader2 size={16} className="animate-spin" /> Reinstating…</>
          ) : (
            <><ShieldCheck size={16} /> Reinstate Access</>
          )}
        </button>
      </div>
    </div>
  </div>
);

export { UsersAdmin };
